/* eslint-disable @typescript-eslint/no-explicit-any */
// Worker/WS chat messages are dynamically shaped (see aiAssistant/CLAUDE.md).
import { useEffect, useRef } from 'react';

import { getQueryKey } from '@/hooks/useQueryKeys';
import { queryClient } from '@/queryclient';
import { getAttackPathIdFromName } from '@/sections/attacks/attackPaths/utils';
import { UniqueQueryKeys } from '@/types';

const SAVE_TOOL = 'save_attack_graph';

const callName = (msg: any): string | null => {
  try {
    const d = JSON.parse(msg.toolUseContent ?? msg.content);
    return d?.Name ?? d?.tool ?? d?.name ?? null;
  } catch {
    return null;
  }
};

const parseSaveResponse = (
  msg: any
): { saved: boolean; name?: string } | null => {
  try {
    const d = JSON.parse(msg.content);
    return {
      saved: d?.saved === true,
      name:
        typeof d?.name === 'string' && d.name.length > 0 ? d.name : undefined,
    };
  } catch {
    return null;
  }
};

/**
 * Marcus now saves attack graphs SERVER-SIDE via the `save_attack_graph` tool.
 * That write happens entirely on the backend, so nothing on the client
 * invalidates the file-list cache the Attack Paths page reads
 * (useMy({ resource:'file', query:'#attack-path-' })). With the global 5m
 * staleTime (queryclient.ts) the page would show stale data until a hard
 * refresh. When a SUCCESSFUL save_attack_graph TOOL RESPONSE (saved === true)
 * lands in the chat stream, invalidate BOTH:
 *   1. the ['MY','file'] list prefix — the same fuzzy, impersonation-agnostic
 *      key the frontend-owned save already uses (ChatAttackGraph.doSave) and
 *      the canonical "file list changed" signal (useFiles.invalidateMyFiles).
 *   2. the saved file's GET_FILE blob — the Attack Paths page renders an
 *      individual attack-graph from the GET_FILE blob cache (getQueryKey.getFile
 *      = [GET_FILE, name, global]), which Marcus's save mutates server-side. The
 *      list-only invalidation would leave that cached blob stale, so an operator
 *      who reopens an edited graph could see stale node/edge data until the
 *      global 5m staleTime expires or a hard refresh. The save response carries
 *      the display `name`, so we scope the invalidation to that file via
 *      [GET_FILE, getAttackPathIdFromName(name)] — an exact prefix of the cached
 *      [GET_FILE, attack-path-<name>, global] key (TanStack Query matches by key
 *      prefix, so the trailing `global` segment still matches). If the response
 *      omits the name we FALL BACK to the whole [GET_FILE] prefix rather than
 *      silently skipping invalidation, so the blob is never left stale.
 * Invalid / overwrite-refused responses carry saved:false and have NOT mutated
 * the file, so they are ignored.
 * Guarded by toolUseId so WS dedup / re-renders fire it at most once per save.
 */
export function useAttackGraphSaveInvalidation(messages: any[]) {
  const processed = useRef<Set<string>>(new Set());

  useEffect(() => {
    // Map save tool-call ids so a response is only honored when it pairs a save call.
    const saveCallIds = new Set(
      messages
        .filter(m => m.role === 'tool call' && callName(m) === SAVE_TOOL)
        .map(m => m.toolUseId)
        .filter(Boolean)
    );

    for (const m of messages) {
      if (
        m.role !== 'tool response' ||
        !m.toolUseId ||
        !saveCallIds.has(m.toolUseId) ||
        processed.current.has(m.toolUseId)
      ) {
        continue;
      }
      const resp = parseSaveResponse(m);
      if (resp?.saved !== true) {
        continue;
      }
      processed.current.add(m.toolUseId);
      void queryClient.invalidateQueries({
        queryKey: getQueryKey.getMy('file'),
      });
      const fileId = resp.name ? getAttackPathIdFromName(resp.name) : undefined;
      void queryClient.invalidateQueries({
        queryKey: fileId
          ? [UniqueQueryKeys.GET_FILE, fileId]
          : [UniqueQueryKeys.GET_FILE],
      });
    }
  }, [messages]);
}
